/**
 * Notification API Service
 * Handles all notification-related API calls
 */

import { PAGINATION } from "@/constants/constants"
import { apiClient } from "@/lib/api-client"

/**
 * Notification data for join request notifications
 */
export interface JoinRequestNotificationData {
  requestId: string
  requesterId: string
  requesterName: string
  requesterUsername?: string
  communityId: string
  communityName: string
  communityHandle?: string
  communityCoverImageUrl?: string
}

/**
 * Notification data for join accepted notifications
 */
export interface JoinAcceptedNotificationData {
  accepterId: string | null
  accepterName: string
  accepterUsername?: string
  communityId: string
  communityName: string
  communityHandle?: string
  communityCoverImageUrl?: string
}

/**
 * Notification data for join rejected notifications
 */
export interface JoinRejectedNotificationData {
  rejecterId: string | null
  rejecterName: string
  rejecterUsername?: string
  communityId: string
  communityName: string
  communityHandle?: string
  communityCoverImageUrl?: string
  reason?: string
}

/**
 * Notification interface
 */
export interface Notification {
  id: string
  recipientId: string
  type: string
  action: string
  title: string
  message: string
  entityType: string | null
  entityId: string | null
  triggeredBy: string | null
  triggeredByUser?: {
    id: string
    name: string | null
    username: string | null
    profilePictureUrl: string | null
    profile_photos?: Array<{
      id: string
      model: string
      modelId: string
      fileName: string
      fileType: string
      filePath: string
      isCover: boolean
      is_cover_image?: boolean
    }>
  }
  isRead: boolean
  readAt: Date | null
  isArchived: boolean
  archivedAt: Date | null
  deliveryStatus: string
  deliveredAt: Date | null
  data:
    | JoinRequestNotificationData
    | JoinAcceptedNotificationData
    | JoinRejectedNotificationData
    | Record<string, unknown>
    | null
  createdAt: Date
  expiresAt: Date | null
}

/**
 * Paginated notifications response
 */
export interface NotificationsResponse {
  notifications: Notification[]
  total: number
  page: number
  pages: number
}

/**
 * Tab types for notification filtering
 */
export type NotificationTab = "all" | "ritual" | "communities" | "friends" | "you"

/**
 * Get all notifications for the current user
 */
export async function getNotifications(
  page = PAGINATION.DEFAULT_PAGE,
  limit = PAGINATION.DEFAULT_LIMIT,
  tab?: NotificationTab,
): Promise<NotificationsResponse> {
  const response = await apiClient.get<{
    success: boolean
    data: NotificationsResponse
    message: string
  }>("/api/notifications", { params: { page, limit, tab } })

  return response.data.data
}

/**
 * Get unread notification count
 */
export async function getUnreadCount(tab?: NotificationTab): Promise<number> {
  const response = await apiClient.get<{
    success: boolean
    data: { count: number }
    message: string
  }>("/api/notifications/unread-count", { params: { tab } })

  return response.data.data.count
}

/**
 * Get unread notifications
 */
export async function getUnreadNotifications(
  limit = PAGINATION.DEFAULT_LIMIT,
): Promise<{ notifications: Notification[] }> {
  const response = await apiClient.get<{
    success: boolean
    data: { notifications: Notification[] }
    message: string
  }>("/api/notifications/unread", { params: { limit } })

  return response.data.data
}

/**
 * Mark a notification as read
 */
export async function markAsRead(notificationId: string): Promise<Notification> {
  const response = await apiClient.patch<{
    success: boolean
    data: { notification: Notification }
    message: string
  }>(`/api/notifications/${notificationId}/read`)

  return response.data.data.notification
}

/**
 * Mark a notification as unread
 */
export async function markAsUnread(notificationId: string): Promise<Notification> {
  const response = await apiClient.patch<{
    success: boolean
    data: { notification: Notification }
    message: string
  }>(`/api/notifications/${notificationId}/unread`)

  return response.data.data.notification
}

/**
 * Mark all notifications as read
 */
export async function markAllAsRead(): Promise<{ count: number }> {
  const response = await apiClient.patch<{
    success: boolean
    data: { count: number }
    message: string
  }>("/api/notifications/read-all")

  return response.data.data
}

/**
 * Delete a notification (soft delete)
 */
export async function deleteNotification(notificationId: string): Promise<Notification> {
  const response = await apiClient.delete<{
    success: boolean
    data: { notification: Notification }
    message: string
  }>(`/api/notifications/${notificationId}`)

  return response.data.data.notification
}

/**
 * Delete a broadcast notification (removes from all recipients)
 */
export async function deleteBroadcastNotification(
  notificationId: string,
): Promise<{ deletedCount: number; recipientIds: string[] }> {
  const response = await apiClient.delete<{
    success: boolean
    data: { deletedCount: number; recipientIds: string[] }
    message: string
  }>(`/api/notifications/${notificationId}/broadcast`)

  return response.data.data
}

/**
 * Broadcast a notification to all followers
 */
export async function broadcastNotification(message: string): Promise<{ jobIds: string[] }> {
  const response = await apiClient.post<{
    success: boolean
    data: { jobIds: string[] }
    message: string
  }>("/api/notifications/broadcast", { message })

  return response.data.data
}
